import axiosApi from "@/services/axios/axios";

import {
  branchParams,
  categoryParams,
  createOrgParams,
  ItemParams,
  memberParams,
} from "@/types/organization";

import { Role } from "@/types/userDetails";
import { OrganizationType } from "@/types/apiResposne";
import { ErrorHelper } from "@/utils/errorHelper";
import { Filter, GroupByFilter } from "@/types/filter";

/**
 * creates a category .
 *
 * @param {categoryParams}  params- The category parameters (usually just the category name).

 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */
export const createCategoryApi = async (
  params: categoryParams,
): Promise<OrganizationType.CreateCategoryResponse> => {
  try {
    const response =
      await axiosApi.post<OrganizationType.CreateCategoryResponse>(
        "/expense/category/create",
        params,
      );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * fetches a category.
 *
 *
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */

export const getCategoryApi =
  async (): Promise<OrganizationType.CategoryFetchResponse> => {
    try {
      const response =
        await axiosApi.get<OrganizationType.CategoryFetchResponse>(
          `/expense/categories`,
        );
      return response.data;
    } catch (error) {
      const actualError = ErrorHelper(error);
      throw new Error(
        Array.isArray(actualError) ? actualError[0] : actualError,
      );
    }
  };

/**
 * deletes a category .
 *
 * @id {number} id- the id of th ecategory to be deleted
 * @role {Role} - the role of the user

 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */

export const deleteCategoryApi = async (
  id: number,
): Promise<OrganizationType.DeleteCategoryResponse> => {
  try {
    const response =
      await axiosApi.delete<OrganizationType.DeleteCategoryResponse>(
        `/expense/category/${id}`,
      );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * updates a category .
 *@params {categoryParams} params- the updated category parameters
 * @id {number} id- the id of the category to be updated
 * @role {Role} - the role of the user

 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */

export const updateCategoryApi = async (
  params: categoryParams,
  id: number,
): Promise<OrganizationType.UpdateCategoryResponse> => {
  try {
    const response =
      await axiosApi.put<OrganizationType.UpdateCategoryResponse>(
        `/expense/category/${id}`,
        params,
      );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * creates a branch .
 *
 * @param {branchParams}  params- The branch parameters (usually just the branch name).

 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */

export const createBranchApi = async (
  id: number,
  params: branchParams,
  role: Role,
): Promise<OrganizationType.CreateOrUpdateBranchResponse> => {
  try {
    if (!role.isSuperAdmin && !role.isOrgAdmin) {
      throw "Access denied";
    }
    const response =
      await axiosApi.post<OrganizationType.CreateOrUpdateBranchResponse>(
        `/organization/${id}/branch/create`,
        params,
      );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * fetches a branch.
 *
 *
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */
export const getBranchApi = async (
  id: number,
): Promise<OrganizationType.BranchFetchResponse> => {
  try {
    const response = await axiosApi.get<OrganizationType.BranchFetchResponse>(
      `/organization/${id}/branches`,
    );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * deletes a branch .
 *
 * @id {number} id- the id of the branch to be deleted
 * @role {Role} - the role of the user

 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */

export const deleteBranchApi = async (
  id: number,
  role: Role,
): Promise<OrganizationType.DeleteBranchResponse> => {
  try {
    if (!role.isSuperAdmin && !role.isOrgAdmin) {
      throw "Access denied";
    }
    const response =
      await axiosApi.delete<OrganizationType.DeleteBranchResponse>(
        `/organization/branch/${id}`,
      );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * updates a branch .
 *@params {branchParams} params- the updated branch parameters
 * @id {number} id- the id of the branch be updated
 * @role {Role} - the role of the user

 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */

export const updateBranchApi = async (
  params: branchParams,
  id: number,
  role: Role,
): Promise<OrganizationType.CreateOrUpdateBranchResponse> => {
  try {
    if (!role.isSuperAdmin && !role.isOrgAdmin) {
      throw "Access denied";
    }
    const response =
      await axiosApi.put<OrganizationType.CreateOrUpdateBranchResponse>(
        `/organization/branch/${id}`,
        params,
      );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * creates a organization .
 *@params {createOrgParams} params- the organization parameters
 * @role {Role} - the role of the user

 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */
export const createOrg = async (
  params: createOrgParams,
  role: Role,
): Promise<OrganizationType.CreateOrg> => {
  try {
    if (!role.isSuperAdmin) {
      throw "Access denied";
    }
    const response = await axiosApi.post<OrganizationType.CreateOrg>(
      "/organization/create",
      params,
    );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * sends a invitation link to the gmail of specified individual .
 *@params {memberParams} params- the email of the invitee
 * @role {Role} - the role of the user

 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */
export const inviteMemberApi = async (
  params: memberParams,
  role: Role,
): Promise<OrganizationType.InviteMember> => {
  try {
    if (!role.isSuperAdmin) {
      throw "Access denied";
    }
    const response = await axiosApi.post<OrganizationType.InviteMember>(
      "/organization/invitations/create",
      params,
    );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

export const invitationsListApi =
  async (): Promise<OrganizationType.Invitations> => {
    try {
      const response = await axiosApi.get<OrganizationType.Invitations>(
        "/organization/invitations",
      );
      return response.data;
    } catch (error) {
      const actualError = ErrorHelper(error);
      throw new Error(
        Array.isArray(actualError) ? actualError[0] : actualError,
      );
    }
  };

/**
 * fetches the members of the organization .
 *
 * @role {Role} - the role of the user


 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */

export const getMembersApi =
  async (): Promise<OrganizationType.FetchMember> => {
    try {
      const response =
        await axiosApi.get<OrganizationType.FetchMember>("/auth/users");
      return response.data;
    } catch (error) {
      const actualError = ErrorHelper(error);
      throw new Error(
        Array.isArray(actualError) ? actualError[0] : actualError,
      );
    }
  };

/**
 * deletes a member of the organization .
 *
 * @role {Role} - the role of the user
 * @id {number} - id of the member to be deleted
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */
export const deleteMemberApi = async (
  role: Role,
  id?: number,
  email?: string,
): Promise<OrganizationType.DeleteMember> => {
  try {
    if (!role.isSuperAdmin && !role.isOrgAdmin) {
      throw "Access denied";
    }

    let url = "";
    if (id) {
      url = `/auth/users/${id}`;
    } else if (email) {
      url = `/auth/users/${email}`;
    } else {
      throw "Either id or email must be provided";
    }

    const response = await axiosApi.delete<OrganizationType.DeleteMember>(url);
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * updates a member of the organization .
 *
 * @role {Role} - the role of the user
 * @id {number} - id of the member to be deleted
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */
export const updateMemberApi = async (
  id: number,
  params: memberParams,
  role: Role,
): Promise<OrganizationType.UpdateMember> => {
  try {
    if (!role.isSuperAdmin && !role.isOrgAdmin) {
      throw "Access denied";
    }
    const response = await axiosApi.put<OrganizationType.UpdateMember>(
      `/auth/users/${id}`,
      params,
    );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * creates a item  .
 *
 * @params {ItemParams}- the parameter required or the item
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */

export const createItemApi = async (
  params: ItemParams,
): Promise<OrganizationType.CreateOrUpdateItemResponse> => {
  try {
    const response =
      await axiosApi.post<OrganizationType.CreateOrUpdateItemResponse>(
        "/expense/item/create",
        params,
      );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * fetches items .
 *
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */
export const getItemApi =
  async (): Promise<OrganizationType.ItemFetchResponse> => {
    try {
      const response =
        await axiosApi.get<OrganizationType.ItemFetchResponse>(
          `/expense/items`,
        );
      return response.data;
    } catch (error) {
      const actualError = ErrorHelper(error);
      throw new Error(
        Array.isArray(actualError) ? actualError[0] : actualError,
      );
    }
  };

/**
 * deletes a item .
 *
 * @role {Role} - the role of the user
 * @id {number} - id of the member to be deleted
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */
export const deleteItemApi = async (
  id: number,
): Promise<OrganizationType.DeleteItemResponse> => {
  try {
    const response = await axiosApi.delete<OrganizationType.DeleteItemResponse>(
      `/expense/item/${id}`,
    );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * updates a tag .
 *
 * @role {Role} - the role of the user
 * @id {number} - id of the member to be deleted
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */
export const updateItemApi = async (
  params: ItemParams,
  id: number,
): Promise<OrganizationType.CreateOrUpdateItemResponse> => {
  try {
    const response =
      await axiosApi.put<OrganizationType.CreateOrUpdateItemResponse>(
        `/expense/item/${id}`,
        params,
      );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * creates a expense.
 *
 * @formData {FormData} - the required parameters of expense
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */

export const createExpenseApi = async (
  formData: FormData,
): Promise<OrganizationType.CreateOrUpdateExpenseResponse> => {
  try {
    const response =
      await axiosApi.post<OrganizationType.CreateOrUpdateExpenseResponse>(
        "/expense/create",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
    //header mltipart/form-data is used to directly send the files and data (images+ text fields) in HTTP request
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * fetches  expenses.
 *
 *
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */
export const getExpensesApi = async (page: number, rawParams: Filter) => {
  try {
    const url =
      page > 1 ? `/expense/expenses?page=${page}` : `/expense/expenses`;

    const params: Partial<Record<keyof Filter, string>> = {};
    for (const key in rawParams) {
      const value = rawParams[key as keyof Filter];
      params[key as keyof Filter] = Array.isArray(value)
        ? value.join(",")
        : String(value);
    }
    const response = await axiosApi.get(url, { params });

    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * updates a expense.
 *
 * @id {number} - the id of the expense to be updated
 * @formData {FormData} - the required parameters of expense
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */
export const updateExpensesApi = async (
  id: number,
  formData: FormData,
): Promise<OrganizationType.CreateOrUpdateExpenseResponse> => {
  try {
    const response =
      await axiosApi.put<OrganizationType.CreateOrUpdateExpenseResponse>(
        `/expense/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * deletes a expense.
 *
 * @id {number}- the id of the expense to be deleted
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */
export const deleteExpenseApi = async (
  id: number,
  role: Role,
): Promise<OrganizationType.DeleteExpenseResponse> => {
  try {
    if (!role.isSuperAdmin) {
      throw "Access denied";
    }
    const response =
      await axiosApi.delete<OrganizationType.DeleteExpenseResponse>(
        `/expense/${id}`,
      );
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * fetches expense data according to group.
 *
 * @params {string}- the category for grouping (i.e., category, branch)
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */
export const graphOrderApi = async (
  rawParams: GroupByFilter,
): Promise<OrganizationType.GroupBy> => {
  try {
    const url = "/expense/dashboard";
    const params: Partial<Record<keyof GroupByFilter, string>> = {};
    for (const key in rawParams) {
      const value = rawParams[key as keyof GroupByFilter];
      params[key as keyof GroupByFilter] = Array.isArray(value)
        ? value.join(",")
        : String(value);
    }
    const response = await axiosApi.get<OrganizationType.GroupBy>(url, {
      params,
    });
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};

/**
 * fetches expense data according to group.
 *
 * @params {string}- the category for grouping (i.e., category, branch)
 * @returns {Promise<any>} - Returns a promise resolving with the response data if valid.
 * @throws {string} - Throws an error message if the request fails.
 */
export const getAllExpensesApi = async (
  rawParams: Filter,
): Promise<OrganizationType.Expense> => {
  try {
    const url = "/expense/no-pagination";
    const params: Partial<Record<keyof Filter, string>> = {};
    for (const key in rawParams) {
      const value = rawParams[key as keyof Filter];
      params[key as keyof Filter] = Array.isArray(value)
        ? value.join(",")
        : String(value);
    }
    const response = await axiosApi.get<OrganizationType.Expense>(url, {
      params,
    });
    return response.data;
  } catch (error) {
    const actualError = ErrorHelper(error);
    throw new Error(Array.isArray(actualError) ? actualError[0] : actualError);
  }
};
